import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/apiClient";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Eye, EyeOff, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Field } from "./common";

export function MetricCard({ metric, editing, onEdit, onCloseEdit, onSaved, onToggleVisible }) {
  const [form, setForm] = useState({
    custom_label: metric.effective_label !== metric.default_label ? metric.effective_label : "",
    custom_value_override: metric.effective_value !== metric.computed_value ? String(metric.effective_value) : "",
    sort_order: metric.sort_order ?? 999,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      custom_label: metric.effective_label !== metric.default_label ? metric.effective_label : "",
      custom_value_override: metric.effective_value !== metric.computed_value ? String(metric.effective_value) : "",
      sort_order: metric.sort_order ?? 999,
    });
  }, [metric.key, metric.effective_label, metric.default_label, metric.effective_value, metric.computed_value, metric.sort_order]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        is_visible: metric.is_visible,
        custom_label: form.custom_label?.trim() || null,
        custom_value_override:
          form.custom_value_override !== "" ? parseInt(form.custom_value_override, 10) : null,
        sort_order: parseInt(form.sort_order, 10) || 0,
      };
      await api.patch(`/admin/landing/metrics/${metric.key}`, payload);
      toast.success("Métrica actualizada");
      onCloseEdit();
      await onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const unitLabel = metric.unit === "hours" ? "h" : metric.unit === "percent" ? "%" : "";

  return (
    <Card className="border-gray-200 rounded-sm">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <code className="text-[10px] uppercase tracking-widest bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">
              {metric.key}
            </code>
            {metric.is_visible ? (
              <Badge className="rounded-sm bg-primary/10 text-primary border border-primary/30">Visible</Badge>
            ) : (
              <Badge className="rounded-sm bg-gray-50 text-gray-500 border border-gray-300">Oculto</Badge>
            )}
          </div>
          <div className="flex gap-1">
            <button onClick={onToggleVisible} className="p-1.5 text-gray-500 hover:text-primary" title="Mostrar/ocultar" data-testid={`btn-toggle-${metric.key}`}>
              {metric.is_visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
            {!editing && (
              <button onClick={onEdit} className="p-1.5 text-gray-500 hover:text-primary" title="Editar override" data-testid={`btn-edit-${metric.key}`}>
                <Pencil className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div>
          <div className="text-xs text-gray-500 uppercase tracking-widest mb-1">{metric.effective_label}</div>
          <div className="flex items-baseline gap-2">
            <span className="font-serif text-3xl text-primary font-semibold" data-testid={`metric-value-${metric.key}`}>
              {metric.effective_value?.toLocaleString("es-CO")}
            </span>
            {unitLabel && <span className="text-sm text-gray-600">{unitLabel}</span>}
          </div>
          {metric.effective_value !== metric.computed_value && (
            <p className="text-[11px] text-amber-700 mt-1">
              Override activo. Valor real calculado: {metric.computed_value?.toLocaleString("es-CO")}
            </p>
          )}
        </div>

        {editing && (
          <div className="space-y-3 pt-2 border-t border-gray-100" data-testid={`edit-metric-${metric.key}`}>
            <Field label="Etiqueta personalizada (opcional)">
              <input
                type="text"
                value={form.custom_label}
                onChange={(e) => setForm({ ...form, custom_label: e.target.value })}
                className="input-base"
                maxLength={80}
                placeholder={metric.default_label}
              />
            </Field>
            <Field label="Valor override (opcional — deja vacío para usar el valor calculado)">
              <input
                type="number"
                min={0}
                value={form.custom_value_override}
                onChange={(e) => setForm({ ...form, custom_value_override: e.target.value })}
                className="input-base"
                placeholder={String(metric.computed_value)}
              />
            </Field>
            <Field label="Orden">
              <input
                type="number"
                min={0}
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })}
                className="input-base w-28"
              />
            </Field>
            <div className="flex gap-2 pt-2">
              <Button onClick={save} disabled={saving} className="rounded-sm bg-primary" data-testid={`btn-save-${metric.key}`}>
                {saving ? "Guardando…" : "Guardar"}
              </Button>
              <Button variant="outline" onClick={onCloseEdit} className="rounded-sm border-gray-300" data-testid={`btn-cancel-${metric.key}`}>
                Cancelar
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
